import { ReactNode } from 'react';
import Divider from '@mui/material/Divider';
import { styled, Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Row, Column } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import Menu, { TabsMenuProps } from './Menu';
import Box from '@mui/material/Box';
import FormField from 'further-ui/components/FormField';

const StyledContainer = styled(Box)(() => ({
  width: '244px',
  flexShrink: 0,
}));

type VerticalTabsProps<T extends string> = {
  spacing?: keyof Theme['spacingSizes'];
  fullHeight?: boolean;
  responsive?: boolean;
  renderContent: (tab: T) => ReactNode;
} & TabsMenuProps<T>;

const VerticalTabs = <T extends string>({
  tabs,
  selectedTab,
  spacing = 'xl',
  fullHeight,
  responsive,
  menuProps,
  onTabChange,
  renderContent,
}: VerticalTabsProps<T>): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (responsive && isMobile) {
    return (
      <Column spacing="lg" fullWidth>
        <FormField size="md">
          <BasicSelect<T>
            value={selectedTab}
            onChange={onTabChange}
            fullWidth
            options={tabs.map((tab) => ({
              value: tab.id as T extends Array<infer U> ? U : T,
              label: tab.label,
            }))}
          />
        </FormField>
        <Divider />
        {renderContent(selectedTab)}
      </Column>
    );
  }

  return (
    <Row spacing={spacing} fullWidth height={fullHeight ? '100%' : undefined}>
      <StyledContainer>
        <Menu
          menuProps={menuProps}
          tabs={tabs}
          selectedTab={selectedTab}
          onTabChange={onTabChange}
        />
      </StyledContainer>

      <Divider orientation="vertical" flexItem />
      {renderContent(selectedTab)}
    </Row>
  );
};

export default VerticalTabs;
